import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext.jsx';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './Auth.css';

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    username: ''
  });
  const [error, setError] = useState('');
  const [socialUrls, setSocialUrls] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Check for login error from URL params
    const loginError = searchParams.get('error');
    if (loginError) {
      setError('Login failed. Please try again.');
    }

    // Fetch social login URLs
    fetchSocialUrls();
  }, [searchParams]);

  const fetchSocialUrls = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/auth/social/urls`);
      if (response.ok) {
        const urls = await response.json();
        setSocialUrls(urls);
      }
    } catch (error) {
      console.error('Error fetching social login URLs:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
      const response = await axios.post(`${import.meta.env.VITE_API_URL}${endpoint}`, formData);
      login(response.data.user, response.data.token);
      // Redirect to dashboard after successful login/signup
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || `An error occurred during ${isLogin ? 'login' : 'sign up'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOIDCLogin = () => {
    // Redirect to backend OIDC login
    window.location.href = `${import.meta.env.VITE_API_URL}/login`;
  };

  const handleSocialLogin = (provider) => {
    if (socialUrls[provider]) {
      window.location.href = socialUrls[provider];
    } else {
      setError(`${provider} login is not available yet.`);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setError('');
    // Do not reset formData here; preserve user input when switching modes
  };

  return (
    <div className="auth-container">
      <h2>{isLogin ? 'LOGIN' : 'SIGN UP'}</h2>
      {error && <div className="error-message">{error}</div>}
      
      {/* OIDC Login Button */}
      <div className="auth-section">
        <button 
          type="button" 
          className="oidc-button"
          onClick={handleOIDCLogin}
          disabled={isLoading}
        >
          <span className="oidc-icon">🔐</span>
          Continue with Cognito
        </button>
      </div>

      {/* Social Login Buttons */}
      <div className="auth-section">
        <div className="social-buttons">
          <button 
            type="button" 
            className="social-button google"
            onClick={() => handleSocialLogin('google')}
            disabled={isLoading}
          >
            <span className="social-icon">🔍</span>
            Continue with Google
          </button>
          
          <button 
            type="button" 
            className="social-button facebook disabled"
            disabled={true}
            title="Facebook login coming soon"
          >
            <span className="social-icon">📘</span>
            Continue with Facebook
            <span className="coming-soon">Coming Soon</span>
          </button>
        </div>
      </div>

      {/* Divider */}
      <div className="auth-divider">
        <span>or</span>
      </div>

      {/* Traditional Email/Password Form */}
      <form onSubmit={handleSubmit}>
        {!isLogin && (
          <div className="form-group">
            <label htmlFor="username">Username:</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              placeholder="Choose a username"
              disabled={isLoading}
            />
          </div>
        )}
        <div className="form-group">
          <label htmlFor="email">Email:</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder={isLogin ? "Enter your email" : "Enter your email address"}
            disabled={isLoading}
          />
        </div>
        <div className="form-group">
          <label htmlFor="password">Password:</label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
            minLength="6"
            placeholder={isLogin ? "Enter your password" : "Choose a password (min. 6 characters)"}
            disabled={isLoading}
          />
        </div>
        <button 
          type="submit" 
          className="submit-button"
          disabled={isLoading}
        >
          {isLoading ? 'Loading...' : (isLogin ? 'Login' : 'Sign Up')}
        </button>
      </form>

      <div className="auth-toggle">
        <span 
          className="toggle-text"
          onClick={toggleMode}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              toggleMode();
            }
          }}
        >
          {isLogin ? "Don't have an account? Sign Up" : "Already have an account? Login"}
        </span>
      </div>
    </div>
  );
};

export default Auth; 